import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  itemType?: string;
  itemName?: string;
  isDeleting?: boolean;
  dangerLevel?: 'low' | 'medium' | 'high';
}

export const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Deletion",
  message,
  itemType = "item",
  itemName = "",
  isDeleting = false,
  dangerLevel = 'medium'
}) => {
  const { darkMode } = useTheme();
  
  if (!isOpen) return null;
  
  const defaultMessage = `Are you sure you want to delete this ${itemType}${itemName ? ` "${itemName}"` : ''}? This action cannot be undone.`;
  
  // Determine color based on danger level
  const getBgColor = () => {
    if (dangerLevel === 'high') {
      return darkMode ? 'bg-red-900 bg-opacity-30 border-red-700 border-2' : 'bg-red-50 border-red-300 border-2';
    } else if (dangerLevel === 'medium') {
      return darkMode ? 'bg-red-900 bg-opacity-20 border-red-800' : 'bg-red-50 border-red-200';
    } else if (dangerLevel === 'low') {
      return darkMode ? 'bg-yellow-900 bg-opacity-20 border-yellow-800' : 'bg-yellow-50 border-yellow-200';
    }
    return darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${getBgColor()} p-6 rounded-lg shadow-lg max-w-md w-full border`}>
        <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>{title}</h2>
        <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          {message || defaultMessage}
        </p>
        
        {dangerLevel === 'high' && (
          <div className={`mb-6 p-3 rounded ${darkMode ? 'bg-red-900 bg-opacity-30 text-red-200 border border-red-700' : 'bg-red-100 text-red-800 border border-red-300'}`}>
            <strong className="text-red-500">⚠️ Warning:</strong> This is a destructive action that may have significant consequences and cannot be undone.
          </div>
        )}
        
        {dangerLevel === 'medium' && (
          <div className={`mb-6 p-3 rounded ${darkMode ? 'bg-amber-900 bg-opacity-30 text-amber-200' : 'bg-amber-100 text-amber-800'}`}>
            <strong>Caution:</strong> This action cannot be undone.
          </div>
        )}
        
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-md ${
              darkMode 
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
            }`}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 ${
              dangerLevel === 'high'
                ? 'bg-red-700 hover:bg-red-800 text-white font-bold'
                : 'bg-red-600 hover:bg-red-700 text-white'
            } rounded-md disabled:opacity-50 ${
              dangerLevel === 'high' ? 'border-2 border-red-500' : ''
            }`}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : dangerLevel === 'high' ? 'Delete Permanently' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};
